<template>
  <onboarding-form-layout
    :title="$t('incremental_onboarding.bankAccountForPayouts.title')"
    :description="$t('incremental_onboarding.bankAccountForPayouts.subtitle')"
    :loading="submitting"
    :show-previous="!isUpdate"
    :proceed-label=" addBankAccount ? $t('global.action.submit') : $t('incremental_onboarding.submit_application')"
    @proceed="proceed"
  >
    <ErrorCodeNotification v-if="errorCodes && errorCodes.length > 0" :error-codes="errorCodes" />
    <!--
    <div class="s-flex s-justify-start s-items-center s-gap-2 s-font-medium" v-if="!addBankAccount">
      <s-checkbox
        label="I'll add bank account details later."
        v-model="skip"
        class="s-font-medium"
      />
    </div>
    -->

    <s-notification variant="primary" width="full" v-if="skip">
      Add your bank account as soon as you begin processing so you can receive funds.
    </s-notification>
    <InputField
      v-if="shouldShowApiField('name')"
      :label="
        $t(
          'incremental_onboarding.bankAccountForPayouts.accountHolderName.label'
        )
      "
      :value="name"
      :show-mandatory="true"
      :disabled="skip"
      class-name="s-flex-1 s-w-full"
      id="account-holder-name"
      data-testid="account-holder-name"
      :set-value="setName"
      :error="errors.name"
    />
    <div
      class="s-flex s-flex-col s-items-start s-justify-start"
      v-if="shouldShowApiField('accountType')"
    >
      <span>
        <s-label
          :label="
            $t('incremental_onboarding.bankAccountForPayouts.accountType.label')
          "
          class="s-font-medium"
          :class="{'s-text-neutral-400': skip}"
        />
        <span class="s-text-danger-600">*</span>
      </span>

      <div class="radio-group s-mt-2 s-flex s-gap-6">
        <template v-for="type in getAccountTypes()">
          <s-label :key="type.value" :class="{'s-text-neutral-400': skip}">
            <input
              type="radio"
              name="accountType"
              value="private"
              :disabled="skip"
              @change="setAccountType(type.value)"
              :checked="accountType === type.value"
            />
            {{ type.displayText }}
          </s-label>
        </template>
      </div>
      <c-inline-error
        v-if="Boolean(getErrorByField(BankAccountField.ACCOUNT_TYPE))"
        :message="getErrorByField(BankAccountField.ACCOUNT_TYPE)"
      />
    </div>

    <template v-for="accountNumberField in ['accountNumber', 'iban']">
      <div
        :key="accountNumberField"
        class="s-flex s-flex-col s-gap-4"
        v-if="!readOnly && shouldShowApiField(accountNumberField)"
      >
        <InputField
          :label="
            $t(
              'incremental_onboarding.bankAccountForPayouts.' +
                accountNumberField +
                '.label'
            )
          "
          :value="
            getCountrySpecificBankAccountApiFields[accountNumberField].value
          "
          type="password"
          id="account-number"
          class-name="s-flex-1 s-w-full"
          data-testid="account-number"
          :disabled="skip"
          :show-mandatory="true"
          :error="
            getErrorByField(BankAccountField.ACCOUNT_NUMBER) ||
              getErrorByField(BankAccountField.IBAN)
          "
          :set-value="
            (event) =>
              setCountrySpecificBankField({ field: accountNumberField, event })
          "
          class="fs-mask"
        />

        <InputField
          :label="
            $t(
              'incremental_onboarding.bankAccountForPayouts.reenterAccountNumber.' +
                accountNumberField +
                '.label'
            )
          "
          :value="reenterAccountNumber"
          :error="reenterAccountNumber ? errors.reenterAccountNumber : null"
          :block-copy-paste="true"
          :show-mandatory="true"
          :disabled="skip"
          id="reenter-account-number"
          data-testid="reenter-account-number"
          :set-value="setReenterAccountNumber"
          class="fs-mask"
          class-name="s-flex-1 s-w-full"
          v-if="shouldShowApiField('reenterAccountNumber')"
        />
      </div>
    </template>

    <template v-for="bankCodeField in ['bankCode', 'sortCode']">
      <div
        :key="bankCodeField"
        class="s-flex s-flex-col"
        v-if="!readOnly && shouldShowApiField(bankCodeField)"
      >
        <InputField
          :label="
            $t(
              'incremental_onboarding.bankAccountForPayouts.' +
                bankCodeField +
                '.label'
            )
          "
          :value="getCountrySpecificBankAccountApiFields[bankCodeField].value"
          :set-value="
            (event) =>
              setCountrySpecificBankField({ field: bankCodeField, event })
          "
          id="bank-code"
          :disabled="skip"
          data-testid="bank-code"
          :show-mandatory="true"
          :error="
            getErrorByField(BankAccountField.BANK_CODE) ||
              getErrorByField(BankAccountField.SORT_CODE)
          "
          :hint="
            $t(
              'incremental_onboarding.bankAccountForPayouts.' +
                bankCodeField +
                '.instruction'
            )
          "
          class="fs-mask"
          class-name="s-flex-1 s-w-full"
        />
      </div>
    </template>
  </onboarding-form-layout>
</template>

<script>
import merchantOnboardingApi from '@/api/merchantOnboardingApi';
import InputField from '@/components/common/InputField';
import OnboardingFormLayout from '@/components/incremental-onboarding/OnboardingFormLayout.vue';
import { OnboardingFormMixin } from '@/components/incremental-onboarding/OnboardingFormMixin';
import { BankAccountField } from '@/store/modules/onboarding/bankAccountForPayouts';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import { bankTransferType } from "@/store/modules/onboarding/countrySpecificFieldsUtil";
import { callbackApplication } from "@/app/utils/common/functions/functions";
import ErrorCodeNotification from "@/components/incremental-onboarding/ErrorCodeNotification.vue";
import { OnboardingStep } from '@/app/utils/common/constants';
import FullStoryHelper from '@/app/utils/fullstory';
import {BANK_ACCOUNT_PROCEED, UPDATE_BANK_ACCOUNT_PROCEED} from '@/app/utils/fullstory/constants';

export default {
  name: 'IncrementalOnboardingBankAccountForPayout',
  components: { ErrorCodeNotification, InputField, OnboardingFormLayout },
  mixins: [OnboardingFormMixin],
  props: {
    readOnly: {
      type: Boolean,
      required: false,
      default: false,
    },
    addBankAccount: {
      type: Boolean,
      required: false,
      default: false,
    },
    isUpdate: {
      type: Boolean,
      required: false,
      default: false,
    },
    errorCodes: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
  data: () => ({
    skip: false,
  }),
  computed: {
    BankAccountField() {
      return BankAccountField;
    },
    ...mapGetters('bankAccountForPayouts', [
      'getAccountTypeError',
      'getErrorByField',
      'getCountrySpecificBankAccountApiFields',
      'getIncrementalOnboardingData',
      'isDirty',
      'shouldShowApiField',
    ]),
    ...mapGetters('incrementalOnboardingBusinessAddress', ['getCountry']),
    ...mapGetters('app', ['getMerchantId','getCallbackUrl']),
    ...mapState({
      errors: (state) => state.bankAccountForPayouts.errors,
      name: (state) => state.bankAccountForPayouts.data.name.value,
      nameApiPath: (state) => state.bankAccountForPayouts.data.name.apiPath,
      accountType: (state) =>
        state.bankAccountForPayouts.data.accountType.value,
      reenterAccountNumber: (state) =>
        state.bankAccountForPayouts.data.reenterAccountNumber.value,
    }),
  },
  mounted() {
    this.setBankTransferType(bankTransferType(this.getCountry))
    if (this.addBankAccount) {
      this.resetName(null);
      this.resetCountrySpecificBankField({
        field: 'accountNumber',
        value: null,
      });
      this.resetCountrySpecificBankField({ field: 'iban', value: null });
      this.resetCountrySpecificBankField({ field: 'bankCode', value: null });
      this.resetCountrySpecificBankField({ field: 'sortCode', value: null });
      this.resetAccountType(null);
      this.resetReenterAccountNumber(null);
    }
  },
  methods: {
    ...mapActions('bankAccountForPayouts', {
      setName: 'updateName',
      setAccountType: 'updateAccountType',
      setReenterAccountNumber: 'updateReenterAccountNumber',
      setCountrySpecificBankField: 'updateCountrySpecificBankField',
      validate: 'validate',
      setErrors: 'setErrors',
      resetDirty: 'resetDirty',
      setBankTransferType: 'updateBankTransferType',
    }),
    ...mapMutations('bankAccountForPayouts', {
      resetName: 'updateName',
      resetAccountType: 'updateAccountType',
      resetReenterAccountNumber: 'updateReenterAccountNumber',
      resetCountrySpecificBankField: 'updateCountrySpecificBankField',
    }),
    async proceed() {
      const apiCall = async () => {
        if (this.isUpdate) {
          return await merchantOnboardingApi.updateBankAccount(
            this.getMerchantId,
            {
              ...this.getIncrementalOnboardingData
            }
          );
        }
        return await merchantOnboardingApi.addBankAccount(
          this.getMerchantId,
          {
            skip: this.skip,
            ...this.getIncrementalOnboardingData
          }
        );
      };

      // Emit FullStory event
      FullStoryHelper.emitIncrementalOnboardingEvent(BANK_ACCOUNT_PROCEED, UPDATE_BANK_ACCOUNT_PROCEED, this.isUpdate);

      // to fix while working on add bank account later - SUBMITTED_SKIPPED_BANK_ACCOUNT
      const successCallBack = this.isUpdate ? undefined : () => callbackApplication(this.getCallbackUrl, this.getMerchantId,
        this.getAutoSavedDraftMerchantId, OnboardingStep.SUBMITTED_ADDED_BANK_ACCOUNT);

      await this.submitForm(apiCall, successCallBack, this.skip);
    },
    getAccountTypes: () => [
      {
        displayText: 'Savings',
        value: 'SAVINGS',
      },
      {
        displayText: 'Checking',
        value: 'CHECKING',
      },
    ],
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss">
.incremental-bap-container {
  text-align: left;
  width: 100%;
  max-width: 700px;
  align-items: start;
  display: flex;
  flex-direction: column;

  & .header {
    margin-bottom: 8px;
  }

  & .header-add-bank-acc {
    font-size: 20px;
    font-weight: $weight_medium;
    color: $neutral_800;
    margin-bottom: 0;
    text-align: left;
  }

  & .subtitle {
    font-size: 12px;
    color: $neutral_600;
  }
}
</style>

<style src="../../../node_modules/v-calendar/lib/v-calendar.min.css" />
